import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
  useMemo,
  useImperativeHandle,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { FixedSizeGrid } from "react-window";
import { useResizeDetector } from "react-resize-detector";
import { useDndScrolling } from "react-dnd-scrolling";
import ProductTileWrapper from "../ProductTileWrapper";
import {
  setPinnedUnPinnedProducts,
  updateProductListInState,
  updateProductListSequenceMode,
  setSelectedItems,
  setLockedUnlockedProducts,
} from "../../store/product-list/ProductListActions";
import { ProductSequenceData } from "../../store/product-list/ProductListTypes";
import {
  OVERLAY_TABS,
  QUICK_MOVE_DIRECTION,
  SEQUENCE_MODE_TYPE,
} from "../../utils/Constants";
import AppState from "../../store/AppState";
import {
  selectIsContentSlotLoading,
  selectIsFetchingProductIds,
  selectLockedProductInCurrentCategory,
  selectPinnedProductIds,
  selectStoredPinnedProductIds,
  selectStoredUnpinnedProductIds,
  selectSelectedItems,
  selectUpdatedLocksInCategory,
  selectOriginalProductSequences,
} from "../../store/product-list/ProductListSelectors";
import { ViewContext } from "../../context/view-context";
import { DragItem } from "../ProductTile";
import { useOnClickOutside } from "../../hooks/useOnClickOutside";
import {
  resetAllProducAnalyticsView,
  updateProductListBySearch,
} from "../../store/product/ProductActions";
import { selectGetProductIds } from "../../store/product-list/ProductListSelectors";
import {
  selectIsFetchedBySearch,
  selectProducts,
  selectRecentlyAddedNewProducts,
} from "../../store/product/ProductSelectors";
import { PRODUCT_TILE_HEIGHTS } from "../../utils/Constants";
import { useQuery } from "../../hooks/useQueryParams";
import { CircularProgress } from "@material-ui/core";
import useClipboard from "../../hooks/useClipBoard";
import {
  selectStoresLoadedState,
} from "../../store/store-list/StoreListSelectors";
import { StyledDiv, styleClasses } from "./ProductListStyles";
import { selectOverlay } from "store/overlay/OverlaySelectors";
import {
  selectCurrentCatalogId,
  selectIsCatalogsLoading,
} from "store/catalog/CatalogSelectors";
import {
  selectCurrentCategory,
  selectIsTopCategoriesLoading,
} from "store/category/CategorySelectors";
import {
  getContentSlotsBeforeTheCurrentPosition,
  handleProductSequencePositioning,
} from "utils/ProductUtil";
import { AdapterInfoObject } from "utils/AppConfigUtils";
import { selectAdapterInfo } from "store/adapter-config/AdapterConfigSelectors";

export interface SelectedSwatch {
  selectedSwatch: string;
  productImage: string;
}
export interface SelectedSwatchNode {
  [name: string]: SelectedSwatch;
}

interface ContentSlotData {
  slotId: string;
  position: number;
  type: string;
}

interface Props {
  products: ProductSequenceData[];
  contentSlots?: ContentSlotData[];
  pageStart?: number;
  pageEnd?: number;
  totalProducts: ProductSequenceData[];
  searchText?: string;
  currentProductId?: string;
  pageSize?: number;
}

// Add the custom type for the grid ref
interface CustomGridRef extends FixedSizeGrid {
  _outerRef: HTMLDivElement;
  scrollToItem: (params: {
    rowIndex: number;
    columnIndex: number;
    align?: "auto" | "smart" | "center" | "end" | "start";
  }) => void;
}

const getLockedPositions = (
  items: ProductSequenceData[],
  lockedProductSelector: (productId: string, position: number) => any,
) => {
  return items
    .map((item, index) => {
      // We need to pass the actual position (1-based) to the selector
      const position = index + 1;
      return {
        productId: item.productId,
        isLocked: Boolean(lockedProductSelector(item.productId, position)),
        position: index,
      };
    })
    .filter(({ isLocked }) => isLocked);
};

const ProductList = React.forwardRef<
  {
    scrollToProductIndex: (productIndex: number) => void;
  },
  Props
>((props, ref) => {
  const {
    products,
    contentSlots = [],
    pageStart,
    pageEnd,
    totalProducts,
    searchText = "",
    currentProductId,
    pageSize = 24,
  } = props;
  const query = useQuery();
  const categoryPathQuery = query.get("categoryPath") ?? "";
  const selectedItems = useSelector(selectSelectedItems);
  const [draggedItemsIds, setDraggedItemsIds] = useState<string[]>([]);
  const [activeItemId, setActiveItemId] = useState("");
  const [hoveredItemIndex, setHoveredItemIndex] = useState(-1);
  const [insertLineIndex, setInsertLineIndex] = useState(-1);
  const [newSequence, setNewSequence] = useState<any[]>();
  const viewContext = useContext(ViewContext);
  const insertIndexRef = useRef<{ insertIndex: number }>({ insertIndex: -1 });
  const { width, height, ref: resizeRef } = useResizeDetector();
  const isFetchedBySearch = useSelector(selectIsFetchedBySearch);
  const recentlyAddedNewProducts = useSelector(selectRecentlyAddedNewProducts);
  const productIds = useSelector(selectGetProductIds);
  const isStoresLoaded = useSelector(selectStoresLoadedState);
  const isCatalogsLoading = useSelector(selectIsCatalogsLoading);
  const isTopCategoriesLoading = useSelector(selectIsTopCategoriesLoading);
  const isFetchingProductIds = useSelector(selectIsFetchingProductIds);
  const isContentSlotLoading = useSelector(selectIsContentSlotLoading);
  const productById = useSelector(selectProducts);

  const dispatch = useDispatch();
  const { addClipboardProductCodes } = useClipboard();
  const selectedOverlay = useSelector(selectOverlay);

  const categoryId = useSelector(selectCurrentCategory);
  const catalogId = useSelector(selectCurrentCatalogId);
  const storedPinnedProductIds = useSelector(selectStoredPinnedProductIds);
  const updatedLockedProductsInCategory = useSelector((state: AppState) =>
    selectUpdatedLocksInCategory(state, categoryId, catalogId),
  );
  const storedUnpinnedProductIds = useSelector(selectStoredUnpinnedProductIds);
  const pinnedProducts = useSelector(selectPinnedProductIds);
  const originalProductSequences = useSelector(selectOriginalProductSequences);

  const lockedProductSelector = useSelector(
    (state: AppState) => (productId: string, position: number) =>
      selectLockedProductInCurrentCategory(
        state,
        categoryId,
        catalogId,
        productId,
        position,
      ),
  );

  const memoizedContentSlots = useMemo(() => contentSlots, [contentSlots]);
  const adapterInfo: AdapterInfoObject = useSelector(selectAdapterInfo);
  const isUnmanagedProductsSupported = adapterInfo?.capabilities?.includes(
    "unmanaged-products",
  ) as boolean;

  const processProductList = useCallback(
    (productList: ProductSequenceData[]) => {
      if (memoizedContentSlots.length === 0) {
        return productList;
      }

      // First, identify existing content slots and their positions
      const existingContentSlots = productList.reduce(
        (acc, product, index) => {
          if (product.slotId) {
            acc[index + 1] = { ...product, position: index + 1 };
          }
          return acc;
        },
        {} as Record<number, any>,
      );

      // Sort new slots in ascending order
      const sortedSlots = [...memoizedContentSlots].sort(
        (a, b) => a.position - b.position,
      );

      // Filter slots for current page
      const currentPageSlots = sortedSlots.filter((slot) => {
        const adjustedPosition = slot.position - 1 - (pageStart ?? 0);
        return adjustedPosition >= 0 && adjustedPosition < pageSize;
      });

      // Calculate total positions needed for current page
      const totalPositions = productList.length + currentPageSlots.length;

      // Create final array
      const finalList = new Array(totalPositions);

      // Place existing content slots
      Object.values(existingContentSlots).forEach((item) => {
        finalList[item.position - 1] = item;
      });

      // Add new content slots, adjusting for pageStart
      currentPageSlots.forEach((slot) => {
        const adjustedPosition = slot.position - (pageStart ?? 0);
        finalList[adjustedPosition - 1] = {
          ...slot,
          productId: slot.slotId,
        };
      });

      // Fill remaining positions with products
      let productIndex = 0;
      for (let i = 0; i < finalList.length; i++) {
        if (!finalList[i] && productIndex < productList.length) {
          const product = productList[productIndex];
          // Skip if product is already a content slot
          if (!product.slotId) {
            finalList[i] = {
              ...product,
              sequence: productIndex,
            };
            productIndex++;
          }
        }
      }

      // Remove any undefined entries
      return finalList.filter(Boolean);
    },
    [memoizedContentSlots, pageStart, pageSize],
  );

  function findCommonObjectsAndMerge(array1, array2) {
    const commonObjects: any[] = [];
    const uniqueObjects: any[] = [];

    // Create a set of IDs from the second array for faster lookup
    const idSet = new Set(array2?.map((obj) => obj?.productId));
    // Loop through the first array
    for (let obj of array1) {
      // Check if the object's ID exists in the second array
      if (idSet.has(obj?.productId) && obj.sequence === 0) {
        const updatedObj = { ...obj, isRecentlyAdded: true };
        commonObjects.push(updatedObj);
      } else {
        uniqueObjects.push(obj);
      }
    }
    // Merge commonObjects and uniqueObjects
    const mergedArray = [...commonObjects, ...uniqueObjects];

    // To make the list unique, you can use a Set and then convert it back to an array
    const uniqueMergedArray = Array.from(new Set(mergedArray));

    return uniqueMergedArray;
  }

  function doesIdExist(array, targetId) {
    for (const item of array) {
      const id = Object.keys(item)[0];
      if (id === targetId.toString()) {
        return true;
      }
    }
    return false;
  }

  const updatePinnedProduct = useCallback(
    (
      shouldBePinned: boolean | null,
      productId: string,
      updatedPinnedProducts: string[],
    ) => {
      if (shouldBePinned !== null) {
        if (shouldBePinned) {
          updatedPinnedProducts.push(productId);
        } else {
          updatedPinnedProducts = updatedPinnedProducts.filter(
            (id) => id !== productId,
          );
        }
        dispatch(
          setPinnedUnPinnedProducts({
            productId,
            isPinned: shouldBePinned,
            catalogId,
            categoryId,
          }),
        );
      }
      return updatedPinnedProducts;
    },
    [dispatch, catalogId, categoryId],
  );

  // Memoize the sequence update logic
  const updateSequence = useCallback(
    (productList: ProductSequenceData[]) => {
      // Only update if the new sequence is different
      setNewSequence((prevSequence) => {
        const processedList = processProductList(productList);
        // Check if the new sequence is different from the previous one
        if (JSON.stringify(prevSequence) === JSON.stringify(processedList)) {
          return prevSequence;
        }
        return processedList;
      });
    },
    [processProductList],
  );

  React.useEffect(() => {
    if (recentlyAddedNewProducts.length) {
      const categoryTree = categoryPathQuery.split(";");
      const categoryId = categoryTree[categoryTree.length - 1];
      const exists = doesIdExist(recentlyAddedNewProducts, categoryId);
      if (exists) {
        const relevantData = recentlyAddedNewProducts.find(
          (data) => Object.keys(data)[0] === categoryId,
        );

        if (relevantData) {
          const uniqueList = findCommonObjectsAndMerge(
            products,
            relevantData[categoryId],
          );
          updateSequence(uniqueList);
        }
      } else {
        updateSequence(products);
      }
    } else {
      updateSequence(products);
    }
  }, [products, categoryPathQuery, recentlyAddedNewProducts, updateSequence]);

  const hasProductPositionsChanged = useCallback(
    (updatedProductSequenceList: ProductSequenceData[]) => {
      const originalProductMap = new Map(
        originalProductSequences.map((p) => [p.productId, p]),
      );
      const hasNewPositionsFromList = updatedProductSequenceList.some(
        (product) => {
          const originalProduct = originalProductMap.get(product.productId);
          // Check if product has a position now but didn't have one originally
          const positionWasAdded =
            product.position !== undefined &&
            originalProduct?.position === undefined;
          // Check if product had a position originally but the position value changed
          const positionValueChanged =
            product.position !== undefined &&
            originalProduct?.position !== undefined &&
            product.position !== originalProduct.position;
          return positionWasAdded || positionValueChanged;
        },
      );

      return hasNewPositionsFromList;
    },
    [originalProductSequences],
  );

  const resetSelectedItem = useCallback(
    (event: MouseEvent) => {
      // Skip if clicking on pagination elements
      const target = event.target as HTMLElement;
      if (
        target.closest(".MuiPagination-root") ||
        target.closest(".MuiPaginationItem-root")
      ) {
        return;
      }

      if (selectedItems.length > 0) {
        dispatch(setSelectedItems([]));
      }
    },
    [selectedItems.length, dispatch],
  );

  useOnClickOutside(resizeRef, resetSelectedItem);

  const itemMoveHandler = useCallback(
    (hoverIndex: number, insertLineIndex: number) => {
      // Don't allow dragging outside current page boundaries if pagination is active
      if (pageStart !== undefined && pageEnd !== undefined) {
        // Use newSequence.length instead of products.length to account for content slots
        const currentSequenceLength = newSequence?.length ?? products.length;
        if (insertLineIndex < 0 || insertLineIndex > currentSequenceLength) {
          return;
        }
      }
      setHoveredItemIndex(hoverIndex);
      setInsertLineIndex(insertLineIndex);
    },
    [pageStart, pageEnd, products.length, newSequence],
  );

  const itemDragStartHandler = useCallback(
    (dragItem: DragItem) => {
      const filterDraggedItemsFromCurrentPage = dragItem.items?.filter(
        (item) => {
          return products.find(
            (product) => product.productId === item.productId,
          );
        },
      );
      dispatch(setSelectedItems(filterDraggedItemsFromCurrentPage, true));
      setDraggedItemsIds(dragItem.items.map((item) => item.productId));
      setActiveItemId(dragItem.draggedItem.productId);
    },
    [dispatch, products],
  );

  const getpinnedProducts = useCallback(() => {
    return new Set(
      [...storedPinnedProductIds, ...pinnedProducts].filter(
        (product) => !storedUnpinnedProductIds.includes(product),
      ),
    );
  }, [storedPinnedProductIds, pinnedProducts, storedUnpinnedProductIds]);

  const getLastPinnedPosition = (
    items: ProductSequenceData[],
    pinnedIds: Set<string>,
    lockedIds: Set<string>,
    draggedItems: ProductSequenceData[],
  ) => {
    let lastPosition = -1;
    items.forEach((item, index) => {
      if (
        !lockedIds.has(item.productId) &&
        !draggedItems.includes(item) &&
        pinnedIds.has(item.productId)
      ) {
        lastPosition = index;
      }
    });
    return lastPosition;
  };

  const getLastPositionedIndex = (
    items: ProductSequenceData[],
    positionedIds: Set<string>,
    lockedIds: Set<string>,
    draggedItems: ProductSequenceData[],
  ) => {
    let lastPosition = -1;
    items.forEach((item, index) => {
      if (
        !lockedIds.has(item.productId) &&
        !draggedItems.includes(item) &&
        positionedIds.has(item.productId)
      ) {
        lastPosition = index;
      }
    });
    return lastPosition;
  };

  // Function to handle out of position locks
  const handleOutOfPositionLocks = useCallback(
    (
      finalProductSequenceList: ProductSequenceData[],
      updatedPinnedProducts: string[],
    ) => {
      // Find locks that are out of position
      const outOfPositionLocks = updatedLockedProductsInCategory.filter(
        (lock) => {
          const currentIndex = finalProductSequenceList.findIndex(
            (product) => product.productId === lock.productId,
          );
          // Lock position is 1-based, array index is 0-based
          return currentIndex !== lock.position - 1;
        },
      );

      // Remove out of position locks and check if they should be pinned
      if (outOfPositionLocks.length > 0) {
        const unlockActions = outOfPositionLocks.map((lock) => ({
          productId: lock.productId,
          categoryId,
          catalogId,
          position: lock.position,
          isLocked: false,
        }));

        // Dispatch unlock actions
        unlockActions.forEach((unlockAction) => {
          dispatch(setLockedUnlockedProducts(unlockAction));
        });

        // Check if any of these products should be pinned
        outOfPositionLocks.forEach((lock) => {
          const product = finalProductSequenceList.find(
            (p) => p.productId === lock.productId,
          );
          if (product) {
            // Check if product should be pinned based on its new position
            const shouldBePinned = updatedPinnedProducts.includes(
              lock.productId,
            );

            if (shouldBePinned) {
              dispatch(
                setPinnedUnPinnedProducts({
                  productId: lock.productId,
                  isPinned: true,
                  catalogId,
                  categoryId,
                }),
              );
            }
          }
        });
      }
    },
    [dispatch, categoryId, catalogId, updatedLockedProductsInCategory],
  );

  const shouldUpdatePinning = (
    item: ProductSequenceData,
    targetIndex: number,
    originalPosition: number,
    lastPinnedPosition: number,
    remainingItems: ProductSequenceData[],
    dragItem: DragItem,
    lockedIds: Set<string>,
    pinnedIds: Set<string>,
  ) => {
    // Skip if returning to original position or if there are no pinned products
    if (targetIndex === originalPosition || pinnedIds.size === 0) return null;

    // If the item was originally pinned and is being placed within the pinned area,
    // keep it pinned
    const wasOriginallyPinned = pinnedIds.has(item.productId);
    if (wasOriginallyPinned) {
      // Check if we're moving within the pinned area
      let isMovingWithinPinnedArea = true;
      for (let i = 0; i < targetIndex; i++) {
        const currentItem = remainingItems[i];
        if (
          currentItem &&
          !pinnedIds.has(currentItem.productId) &&
          !lockedIds.has(currentItem.productId)
        ) {
          isMovingWithinPinnedArea = false;
          break;
        }
      }
      if (isMovingWithinPinnedArea) {
        return true;
      }
    }

    // If the item was originally pinned and is being placed at the last pinned position,
    // keep it pinned
    if (wasOriginallyPinned && targetIndex - 1 === lastPinnedPosition) {
      return true;
    }

    // Check if we're placing after an unpinned product
    const isAfterUnpinnedProduct =
      targetIndex > 0 &&
      !pinnedIds.has(remainingItems[targetIndex - 1]?.productId) &&
      !lockedIds.has(remainingItems[targetIndex - 1]?.productId);

    // If placing after an unpinned product, unpin the item
    if (isAfterUnpinnedProduct) {
      return false;
    }

    // Check for pinned items after target position
    let hasPinnedItemsAfter = false;
    for (let i = targetIndex + 1; i < remainingItems.length; i++) {
      const currentItem = remainingItems[i];
      if (
        !dragItem.items.includes(currentItem) &&
        !lockedIds.has(currentItem.productId) &&
        pinnedIds.has(currentItem.productId)
      ) {
        hasPinnedItemsAfter = true;
        break;
      }
    }

    const shouldBePinned =
      targetIndex <= lastPinnedPosition || hasPinnedItemsAfter;
    return shouldBePinned;
  };

  const shouldUpdatePositioning = (
    item: ProductSequenceData,
    targetIndex: number,
    originalPosition: number,
    lastPositionedIndex: number,
    remainingItems: ProductSequenceData[],
    dragItems: ProductSequenceData[],
    lockedIds: Set<string>,
    positionedIds: Set<string>,
  ) => {
    // If returning to the same position, maintain current positioning status
    if (targetIndex === originalPosition) {
      const isCurrentlyPositioned = positionedIds.has(item.productId);
      return isCurrentlyPositioned ? true : null;
    }

    const wasOriginallyPositioned = positionedIds.has(item.productId);
    const isLocked = lockedIds.has(item.productId);

    // If locked, keep current positioning status
    if (isLocked) {
      return wasOriginallyPositioned;
    }

    // Early return for items placed after unpositioned products
    if (targetIndex > 0) {
      const previousItem = remainingItems[targetIndex - 1];
      if (
        previousItem &&
        !positionedIds.has(previousItem.productId) &&
        !lockedIds.has(previousItem.productId)
      ) {
        return false;
      }
    }

    // Check if we're moving within the positioned area
    if (wasOriginallyPositioned) {
      let isMovingWithinPositionedArea = true;
      for (let i = 0; i < targetIndex; i++) {
        const currentItem = remainingItems[i];
        if (
          currentItem &&
          !positionedIds.has(currentItem.productId) &&
          !lockedIds.has(currentItem.productId)
        ) {
          isMovingWithinPositionedArea = false;
          break;
        }
      }
      if (isMovingWithinPositionedArea) {
        return true;
      }
    }

    // Check for positioned items after target position
    for (let i = targetIndex + 1; i < remainingItems.length; i++) {
      const currentItem = remainingItems[i];
      if (
        currentItem &&
        !dragItems.includes(currentItem) &&
        !lockedIds.has(currentItem.productId) &&
        positionedIds.has(currentItem.productId)
      ) {
        return true;
      }
    }

    return targetIndex <= lastPositionedIndex;
  };

  const dragCompleteHandler = useCallback(
    (dragItem: DragItem, index: number) => {
      if (dragItem) {
        let copiedItems = totalProducts.slice();
        let draggedItems = dragItem.items;
        // When pagination is active, adjust the insert index relative to the current page
        const currentInsertIndex: number = insertIndexRef.current.insertIndex;

        // Don't allow dropping outside current page boundaries
        if (pageStart !== undefined && pageEnd !== undefined) {
          // Use newSequence.length instead of products.length to account for content slots
          const currentSequenceLength = newSequence?.length ?? products.length;
          if (
            currentInsertIndex < 0 ||
            currentInsertIndex > currentSequenceLength
          ) {
            setDraggedItemsIds([]);
            setHoveredItemIndex(-1);
            setInsertLineIndex(-1);
            return;
          }
        }

        const lockedPositions = getLockedPositions(
          copiedItems,
          lockedProductSelector,
        );
        const lockedProductIds = new Set(
          lockedPositions.map((pos) => pos.productId),
        );

        // Remove dragged items from the array
        let remainingItems = copiedItems.filter(
          (item) =>
            !draggedItems.find(
              (draggedItem) => draggedItem.productId === item.productId,
            ),
        );

        // Calculate the global insert index by adding pageStart
        const globalInsertIndex =
          pageStart !== undefined
            ? pageStart + currentInsertIndex
            : currentInsertIndex;

        const constSlotsBeforeInsertIndex =
          getContentSlotsBeforeTheCurrentPosition(
            contentSlots,
            globalInsertIndex + 1,
          );

        // Adjust for content slots
        const adjustedGlobalIndex =
          globalInsertIndex - constSlotsBeforeInsertIndex;

        let newInsertIndex = -1;
        if (adjustedGlobalIndex !== -1) {
          let index = adjustedGlobalIndex;
          do {
            const targetProductId = copiedItems[index]?.productId;
            newInsertIndex = remainingItems.findIndex(
              (item) => item.productId === targetProductId,
            );
            index += 1;
          } while (newInsertIndex < 0 && index < copiedItems.length);
        }
        newInsertIndex =
          newInsertIndex < 0 ? remainingItems.length : newInsertIndex;

        // Create a new array to maintain the sequence with locked positions
        const updatedProductSequenceList = new Array(copiedItems.length);

        // First, place locked items in their original positions
        lockedPositions.forEach(({ productId, position }) => {
          // Find the item in copiedItems that matches this locked position
          const lockedItem = copiedItems.find(
            (item) => item.productId === productId,
          );
          if (lockedItem) {
            // Maintain the original global position
            const globalPosition = position;
            updatedProductSequenceList[globalPosition] = lockedItem;
          }
        });

        // Insert dragged items at the calculated position in remainingItems
        const itemsBeforeInsert = remainingItems.slice(0, newInsertIndex);
        const itemsAfterInsert = remainingItems.slice(newInsertIndex);
        remainingItems = [
          ...itemsBeforeInsert,
          ...draggedItems,
          ...itemsAfterInsert,
        ];

        // Now place all items in the final array, respecting locked positions
        let remainingIndex = 0;
        for (let i = 0; i < updatedProductSequenceList.length; i++) {
          if (updatedProductSequenceList[i] === undefined) {
            // Skip if we've run out of remaining items
            if (remainingIndex >= remainingItems.length) continue;

            // Find the next non-locked item from remainingItems
            while (
              remainingIndex < remainingItems.length &&
              lockedProductIds.has(remainingItems[remainingIndex].productId)
            ) {
              remainingIndex++;
            }

            if (remainingIndex < remainingItems.length) {
              updatedProductSequenceList[i] = remainingItems[remainingIndex];
              remainingIndex++;
            }
          }
        }

        // Filter out any undefined values and ensure type safety
        let finalProductSequenceList: ProductSequenceData[] =
          updatedProductSequenceList.filter(
            (item): item is ProductSequenceData => item !== undefined,
          );

        // Get pinned products
        const filteredPinnedProducts = getpinnedProducts();
        const originalPositions = new Map(
          copiedItems.map((item, index) => [item.productId, index]),
        );

        const lastPinnedPosition = getLastPinnedPosition(
          finalProductSequenceList,
          filteredPinnedProducts,
          lockedProductIds,
          dragItem.items,
        );
        let updatedPinnedProducts = [...filteredPinnedProducts];
        dragItem.items.forEach((item) => {
          const shouldBePinned = shouldUpdatePinning(
            item,
            newInsertIndex,
            originalPositions.get(item.productId) ?? 0,
            lastPinnedPosition,
            finalProductSequenceList,
            dragItem,
            lockedProductIds,
            filteredPinnedProducts,
          );

          updatedPinnedProducts = updatePinnedProduct(
            shouldBePinned,
            item.productId,
            updatedPinnedProducts,
          );
        });
        if (isUnmanagedProductsSupported) {
          // UNMANAGED PRODUCTS LOGIC:
          // 1. Determine which products should have positions based on drag behavior
          // 2. Update position properties while preserving the sequence order
          // 3. Do NOT reorder products - only update their position property

          const draggedProductIds = new Set(
            dragItem.items.map((item) => item.productId),
          );
          const positionedProductIds = new Set<string>();
          const remainingPositionedProductIds = new Set<string>();

          copiedItems.forEach((product) => {
            if (product.position !== undefined) {
              positionedProductIds.add(product.productId);
              if (!draggedProductIds.has(product.productId)) {
                remainingPositionedProductIds.add(product.productId);
              }
            }
          });

          // Calculate the last positioned index in the current sequence
          const lastPositionedIndex = getLastPositionedIndex(
            finalProductSequenceList,
            positionedProductIds,
            lockedProductIds,
            dragItem.items,
          );

          // Process dragged items and build productsToPosition set
          const productsToPosition = new Set<string>();

          // Add remaining positioned products first
          remainingPositionedProductIds.forEach((productId) => {
            productsToPosition.add(productId);
          });

          // Add pinned products
          updatedPinnedProducts.forEach((productId) => {
            productsToPosition.add(productId);
          });

          // Process dragged items
          dragItem.items.forEach((item) => {
            const shouldBePositioned = shouldUpdatePositioning(
              item,
              newInsertIndex,
              originalPositions.get(item.productId) ?? 0,
              lastPositionedIndex,
              finalProductSequenceList,
              dragItem.items,
              lockedProductIds,
              positionedProductIds,
            );

            if (shouldBePositioned === true) {
              productsToPosition.add(item.productId);
            }
          });
          // Handle product sequence positioning and lock management
          finalProductSequenceList = handleProductSequencePositioning(
            finalProductSequenceList,
            productsToPosition,
            updatedLockedProductsInCategory,
            dispatch,
          );
        }

        // Check for locks that are out of position and handle them
        handleOutOfPositionLocks(
          finalProductSequenceList,
          updatedPinnedProducts,
        );

        // Dispatch sequence updates
        if (!isFetchedBySearch) {
          dispatch(
            updateProductListInState({
              products: finalProductSequenceList,
              isSaveAction: true,
            }),
          );

          // Update sequence mode based on the full list
          const isOriginalSequence = finalProductSequenceList.every(
            (e, i) => e.productId === productIds[i],
          );
          const isPinningUnchanged =
            !storedUnpinnedProductIds.some((unpinProduct) =>
              pinnedProducts.includes(unpinProduct),
            ) &&
            !storedPinnedProductIds.some(
              (pinProduct) => !pinnedProducts.includes(pinProduct),
            );
          const hasNewPositions = isUnmanagedProductsSupported
            ? hasProductPositionsChanged(finalProductSequenceList)
            : false;

          dispatch(
            updateProductListSequenceMode(
              isOriginalSequence && isPinningUnchanged && !hasNewPositions
                ? SEQUENCE_MODE_TYPE.VIEW
                : SEQUENCE_MODE_TYPE.EDIT,
            ),
          );
        } else {
          dispatch(updateProductListBySearch(finalProductSequenceList));
        }
      }
      setDraggedItemsIds([]);
      setHoveredItemIndex(-1);
      setInsertLineIndex(-1);
    },
    [
      dispatch,
      insertIndexRef,
      products,
      pageStart,
      pageEnd,
      isFetchedBySearch,
      productIds,
      lockedProductSelector,
      pinnedProducts,
      storedPinnedProductIds,
      contentSlots,
      storedUnpinnedProductIds,
      getpinnedProducts,
      totalProducts,
      isUnmanagedProductsSupported,
      updatePinnedProduct,
      newSequence,
      updatedLockedProductsInCategory,
      handleOutOfPositionLocks,
      hasProductPositionsChanged,
    ],
  );

  const updateProductSequencesByQuickMove = useCallback(
    (productId: string, moveTo: number) => {
      // Get locked products with their global positions (one-based)
      const lockedPositions = totalProducts
        .map((product, index) => ({
          product,
          isLocked: lockedProductSelector(product.productId, index + 1),
          position: index,
        }))
        .filter(({ isLocked }) => isLocked);

      // Create initial list without selected and locked products
      let updatedProductList = totalProducts.filter(
        (product) =>
          !selectedItems.some(
            (selected) => selected.productId === product.productId,
          ) &&
          !lockedPositions.some(
            (locked) => locked.product.productId === product.productId,
          ),
      );

      const insertIndex = updatedProductList.findIndex(
        (product) => product.productId === productId,
      );
      const insertPosition =
        moveTo === QUICK_MOVE_DIRECTION.RIGHT ? insertIndex + 1 : insertIndex;
      const filteredPinnedProducts = getpinnedProducts();

      // Filter out locked products from selection
      const unlockedSelectedProducts = selectedItems.filter(
        (selectedProduct) => {
          const productIndex = totalProducts.findIndex(
            (p) => p.productId === selectedProduct.productId,
          );
          return !lockedProductSelector(
            selectedProduct.productId,
            productIndex + 1,
          );
        },
      );

      const updatedUnlockedSelectedProducts = totalProducts.filter((product) =>
        unlockedSelectedProducts.some(
          (unlockedSelectedProduct) =>
            unlockedSelectedProduct.productId === product.productId,
        ),
      );

      // Store original positions
      const originalPositions = new Map(
        totalProducts.map((item, index) => [item.productId, index]),
      );

      let updatedPinnedProducts = [...filteredPinnedProducts];
      let hasProductsMoved: boolean = false;
      // Insert unlocked selected products and update pinning status
      if (insertPosition >= 0 && updatedUnlockedSelectedProducts.length > 0) {
        updatedProductList.splice(
          insertPosition,
          0,
          ...updatedUnlockedSelectedProducts,
        );

        // Check if products actually moved from their original positions
        hasProductsMoved = updatedUnlockedSelectedProducts?.some((product) => {
          const originalIndex = originalPositions.get(product.productId);
          const newIndex = insertPosition;
          return originalIndex !== newIndex;
        });

        if (isUnmanagedProductsSupported || hasProductsMoved) {
          // Get the last pinned position before the insert position
          let lastPinnedPosition = -1;
          for (let i = 0; i < insertPosition; i++) {
            if (filteredPinnedProducts.has(updatedProductList[i].productId)) {
              lastPinnedPosition = i;
            }
          }

          // Check for pinned products after insert position (excluding selected products)
          let hasPinnedItemsAfter = false;
          updatedProductList.forEach((product, index) => {
            if (
              index > insertPosition &&
              !selectedItems.some(
                (selected) => selected.productId === product.productId,
              ) &&
              filteredPinnedProducts.has(product.productId)
            ) {
              hasPinnedItemsAfter = true;
            }
          });
          // Update pinning status for moved products
          updatedUnlockedSelectedProducts.forEach((product) => {
            const wasOriginallyPinned = filteredPinnedProducts.has(
              product.productId,
            );
            let shouldBePinned: boolean | null =
              (wasOriginallyPinned &&
                insertPosition - 1 === lastPinnedPosition) || // Keep pinned if placed at last pinned position
              insertPosition <= lastPinnedPosition || // Keep pinned if placed before or at last pinned position
              hasPinnedItemsAfter; // Keep pinned if there are pinned items after
            if (filteredPinnedProducts.size === 0) {
              shouldBePinned = null;
            }
            updatedPinnedProducts = updatePinnedProduct(
              shouldBePinned,
              product.productId,
              updatedPinnedProducts,
            );
          });
        }
      }

      let updatedProductSequenceList = new Array(totalProducts.length);

      // Place locked products in their original positions
      lockedPositions.forEach(({ product, position }) => {
        updatedProductSequenceList[position] = product;
      });

      // Fill remaining positions with unlocked products
      let remainingIndex = 0;
      for (let i = 0; i < updatedProductSequenceList.length; i++) {
        if (
          !updatedProductSequenceList[i] &&
          remainingIndex < updatedProductList.length
        ) {
          updatedProductSequenceList[i] = updatedProductList[remainingIndex];
          remainingIndex++;
        }
      }
      if (isUnmanagedProductsSupported) {
        // UNMANAGED PRODUCTS LOGIC:
        // 1. Determine which products should have positions based on drag behavior
        // 2. Update position properties while preserving the sequence order
        // 3. Do NOT reorder products - only update their position property

        // Determine which products are currently positioned
        const positionedProductIds = new Set(
          totalProducts
            .filter((product) => product.position !== undefined)
            .map((product) => product.productId),
        );
        const lockedProductIds = new Set(
          lockedPositions.map((p) => p.product.productId),
        );

        // Build the final positioned products set
        const finalPositionedProducts = new Set<string>();

        // Add remaining positioned products (not being moved)
        positionedProductIds.forEach((productId) => {
          if (
            !updatedUnlockedSelectedProducts.some(
              (p) => p.productId === productId,
            )
          ) {
            finalPositionedProducts.add(productId);
          }
        });

        // Add pinned products
        updatedPinnedProducts.forEach((productId) => {
          finalPositionedProducts.add(productId);
        });

        // Process moved products for positioning logic
        if (updatedUnlockedSelectedProducts.length > 0) {
          // Calculate the last positioned index to determine positioned area boundary
          const lastPositionedIndex = getLastPositionedIndex(
            updatedProductSequenceList,
            positionedProductIds,
            lockedProductIds,
            updatedUnlockedSelectedProducts,
          );

          // Create a working copy of positioned product IDs that gets updated during the loop
          const workingPositionedProductIds = new Set(positionedProductIds);

          // Process each moved product for positioning logic
          updatedUnlockedSelectedProducts.forEach((product) => {
            const insertUpdatedProductPosition =
              updatedProductSequenceList.findIndex(
                (p) => p.productId === product.productId,
              );
            // Use the same positioning logic as drag handler
            const shouldBePositioned = shouldUpdatePositioning(
              product,
              insertUpdatedProductPosition,
              originalPositions.get(product.productId) ?? 0,
              lastPositionedIndex,
              updatedProductSequenceList,
              updatedUnlockedSelectedProducts,
              lockedProductIds,
              workingPositionedProductIds,
            );

            // Apply positioning logic
            if (shouldBePositioned === true) {
              finalPositionedProducts.add(product.productId);
              workingPositionedProductIds.add(product.productId);
            } else if (shouldBePositioned === false) {
              finalPositionedProducts.delete(product.productId);
              workingPositionedProductIds.delete(product.productId);
            }
          });
        }
        // Handle product sequence positioning and lock management
        updatedProductSequenceList = handleProductSequencePositioning(
          updatedProductSequenceList,
          finalPositionedProducts,
          updatedLockedProductsInCategory,
          dispatch,
        );
      }
      // Check for locks that are out of position and handle them
      handleOutOfPositionLocks(
        updatedProductSequenceList,
        updatedPinnedProducts,
      );

      if (!isFetchedBySearch) {
        dispatch(
          updateProductListInState({
            products: updatedProductSequenceList,
            isSaveAction: true,
          }),
        );
        const isOriginalSequence = updatedProductSequenceList.every(
          (e, i) => e.productId === productIds[i],
        );
        const isPinningUnchanged =
          !storedUnpinnedProductIds.some((unpinProduct) =>
            pinnedProducts.includes(unpinProduct),
          ) &&
          !storedPinnedProductIds.some(
            (pinProduct) => !pinnedProducts.includes(pinProduct),
          );
        const hasNewPositions = isUnmanagedProductsSupported
          ? hasProductPositionsChanged(updatedProductSequenceList)
          : false;

        dispatch(
          updateProductListSequenceMode(
            isOriginalSequence && isPinningUnchanged && !hasNewPositions
              ? SEQUENCE_MODE_TYPE.VIEW
              : SEQUENCE_MODE_TYPE.EDIT,
          ),
        );
      } else {
        dispatch(updateProductListBySearch(updatedProductSequenceList));
      }
    },
    [
      dispatch,
      isFetchedBySearch,
      selectedItems,
      lockedProductSelector,
      productIds,
      getpinnedProducts,
      storedPinnedProductIds,
      storedUnpinnedProductIds,
      pinnedProducts,
      totalProducts,
      isUnmanagedProductsSupported,
      updatePinnedProduct,
      updatedLockedProductsInCategory,
      handleOutOfPositionLocks,
      hasProductPositionsChanged,
    ],
  );

  const selectionChangeHandler = useCallback(
    (itemId: string, cmdKeyActive: boolean, shiftKeyActive: boolean) => {
      let newSelectedItemsIds: string[] = [];
      let newActiveItemId;

      // Include both products and content slots in cards
      const cards = [
        ...products,
        ...contentSlots.map((slot) => ({
          ...slot,
          productId: slot.slotId,
        })),
      ];
      let previousSelectedItemsIds = selectedItems
        .map((item) => item.productId || item.slotId)
        .filter((id) => id !== undefined);
      let previousActiveItemId = activeItemId;

      if (cmdKeyActive) {
        if (
          previousSelectedItemsIds.indexOf(itemId) > -1 &&
          itemId !== previousActiveItemId
        ) {
          newSelectedItemsIds = previousSelectedItemsIds.filter(
            (id) => id !== itemId,
          );
        } else {
          newSelectedItemsIds = [...previousSelectedItemsIds, itemId];
        }
      } else if (shiftKeyActive && itemId !== previousActiveItemId) {
        const activeCardIndex = cards.findIndex(
          (c) => c.productId === previousActiveItemId,
        );
        const cardIndex = cards.findIndex((c) => c.productId === itemId);
        const lowerIndex = Math.min(activeCardIndex, cardIndex);
        const upperIndex = Math.max(activeCardIndex, cardIndex);
        newSelectedItemsIds = cards
          .slice(lowerIndex, upperIndex + 1)
          .map((c) => c.productId)
          ?.filter((id) => id !== undefined);
      } else {
        if (previousSelectedItemsIds.indexOf(itemId) > -1) {
          newSelectedItemsIds = [];
          setActiveItemId("");
        } else {
          newSelectedItemsIds = [itemId];
          newActiveItemId = itemId;
        }
      }

      // Get all selected products from allProductsData
      const newSelectedCards = Object.values(productById).filter((product) =>
        newSelectedItemsIds.includes(product.productId),
      );

      if (newActiveItemId) {
        setActiveItemId(newActiveItemId);
      }
      dispatch(setSelectedItems(newSelectedCards));
    },
    [
      activeItemId,
      products,
      selectedItems,
      contentSlots,
      dispatch,
      productById,
    ],
  );

  const keyPressHandler = useCallback(
    (e: KeyboardEvent) => {
      const isCopyShortcutPressed = e.metaKey || e.ctrlKey;
      const isCopyKeyC = e.code === "KeyC";
      const isInputOrTextareaActive =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement;
      const condition =
        selectedItems.length > 0 &&
        isCopyShortcutPressed &&
        !isInputOrTextareaActive &&
        isCopyKeyC;
      if (condition) {
        const payload = {
          productCodes: selectedItems.map(
            (item) => productById[item.productId]?.code,
          ),
        };
        addClipboardProductCodes(payload);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, selectedItems],
  );

  useEffect(() => {
    document.addEventListener("keydown", keyPressHandler);
    return () => {
      document.removeEventListener("keydown", keyPressHandler);
    };
  }, [keyPressHandler]);

  useEffect(() => {
    if (selectedOverlay !== OVERLAY_TABS.ANALYTICS_OVERLAY)
      dispatch(resetAllProducAnalyticsView());
  }, [dispatch, selectedOverlay]);

  const containerWidth = width ?? 0;
  const cwidth = containerWidth + 15;
  const containerHeight = height ?? 0;

  // Update rowCount calculation to include both products and content slots
  const totalItems = newSequence?.length ?? 0;
  const rowCount = Math.ceil(totalItems / viewContext.styles.column);
  const gridRef = useRef<CustomGridRef>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  useDndScrolling(outerRef, {});

  // Add effect to scroll to top when page changes
  useEffect(() => {
    if (outerRef.current) {
      outerRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [pageStart]); // pageStart changes when page changes

  useImperativeHandle(ref, () => ({
    scrollToProductIndex: (productIndex: number) => {
      if (!totalProducts || !gridRef.current) return;

      // Find initial product index
      let globalProductIndex = productIndex;

      if (globalProductIndex === -1) return;

      // Calculate the position within the current page
      const columnCount = viewContext.styles.column;
      const pageIndex = globalProductIndex % pageSize;
      const rowIndex = Math.floor(pageIndex / columnCount);
      const columnIndex = pageIndex % columnCount;

      gridRef.current.scrollToItem({
        rowIndex,
        columnIndex,
        align: "start",
      });
    },
  }));

  return (
    <>
      {!isStoresLoaded ||
      isCatalogsLoading ||
      isTopCategoriesLoading ||
      isFetchingProductIds ||
      isContentSlotLoading ? (
        <StyledDiv className={styleClasses.overlay}>
          <CircularProgress />
        </StyledDiv>
      ) : null}
      <StyledDiv
        className={styleClasses.productList}
        ref={resizeRef}
        fontSize={viewContext.styles.productListFZ}
      >
        {newSequence && Array.isArray(newSequence) && (
          <FixedSizeGrid
            ref={gridRef}
            outerRef={outerRef}
            columnCount={viewContext.styles.column}
            columnWidth={containerWidth / viewContext.styles.column}
            rowCount={rowCount}
            rowHeight={
              PRODUCT_TILE_HEIGHTS.get(viewContext.styles.column) ?? 425
            }
            width={cwidth}
            height={containerHeight}
            itemData={{
              draggedItemsIds,
              newSequence,
              selectedItems,
              onSelectionChange: selectionChangeHandler,
              onItemDragStart: itemDragStartHandler,
              onMove: itemMoveHandler,
              onDragComplete: dragCompleteHandler,
              OnQuickMoveCompleteAction: updateProductSequencesByQuickMove,
              hoveredItemIndex,
              insertLineIndex,
              insertIndexRef,
              pageStart,
              searchText,
              currentProductId,
            }}
            itemKey={({ columnIndex, data, rowIndex }) => {
              const index = rowIndex * viewContext.styles.column + columnIndex;
              const item = data.newSequence[index] ?? {};
              return `${item.productId || item.slotId}_${index}`;
            }}
            style={{ overflowY: "auto", overflowX: "hidden" }}
          >
            {ProductTileWrapper}
          </FixedSizeGrid>
        )}
      </StyledDiv>
    </>
  );
});

export default React.memo(ProductList);
